/* eslint-disable react/jsx-pascal-case */
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { BankLog } from "lib/models";
import { Button } from "components/ui/button";
import toast from "react-hot-toast";
import { apiPost } from "components/Common/Axios";
import { Dialog, DialogContent } from "components/ui/dialog";
import { useDropzone } from "react-dropzone";
import BankLogList, { ListType } from "./BankLogList";
import { Icons } from "components/ui/icons";

export type IssuedType = "new" | "total" | "lecture" | "material";

const BankLogPopupAdd = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const [insertBankLogs, setInsertBankLogs] = useState<BankLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setIsLoading(true);

    try {
      const formData = new FormData();
      acceptedFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await apiPost("/bank/upload", formData, true);
      if (response.error) {
        toast.error("업로드에 실패했습니다");
        console.error("bank upload error", response.error);
        return;
      }

      setInsertBankLogs(response.data);
    } catch (error: any) {
      toast.error("업로드하지 못했습니다");
      console.error("upload error", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setInsertBankLogs([]);
    }
  }, [open]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: { "application/vnd.ms-excel": [".xls", ".xlsx"] },
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] md:max-w-screen-xl max-h-[80%] overflow-x-hidden overflow-y-auto thin-scrollbar pb-10">
          <Card className="border-0 shadow-none">
            <CardHeader className="px-0 pt-0 pb-3 mb-1 sm:px-2 border-b border-gray-100">
              <CardTitle className="text-xl sm:text-2xl font-bold text-center text-gray-900">
                통장 내역 추가
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 sm:px-2 flex flex-col gap-3 text-sm">
              <div
                {...getRootProps()}
                className="border-dashed border-2 border-gray-300 px-4 py-8 text-center cursor-pointer"
              >
                <input {...getInputProps()} />
                {isLoading ? (
                  <div className="flex justify-center items-center">
                    <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                    <p>업로드 중입니다</p>
                  </div>
                ) : (
                  <p>
                    여기에 업로드할 엑셀파일을 드래그 하거나 클릭하고 파일을
                    선택하세요
                  </p>
                )}
              </div>
              {insertBankLogs.length > 0 && (
                <>
                  <div>총 {insertBankLogs.length} 건이 추가되었습니다</div>
                  <BankLogList
                    bankLogs={insertBankLogs}
                    setBankLogs={setInsertBankLogs}
                    listType={ListType.readOnly}
                  />
                </>
              )}
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => {
                    onOpenChange(false);
                  }}
                >
                  닫기
                </Button>
              </div>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BankLogPopupAdd;
